import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";

const normalizeOption = (value) => String(value || "").trim();

const sortOptions = (list) =>
  list
    .filter(Boolean)
    .map((item) => String(item))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

export default function useCommissionCatalog({ enabled = true, filterByProfiles = false } = {}) {
  const [companies, setCompanies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [productsByCompanyId, setProductsByCompanyId] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState({});

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const res = await api.get("/commission/companies");
      const list = Array.isArray(res.data) ? res.data : [];
      setCompanies(list);
    } catch {
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    loadCompanies();
  }, [enabled, loadCompanies]);

  const loadProfiles = useCallback(async () => {
    if (!filterByProfiles) return;
    setLoadingProfiles(true);
    try {
      const res = await api.get("/commission/profiles");
      setProfiles(Array.isArray(res.data) ? res.data : []);
    } catch {
      setProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  }, [filterByProfiles]);

  useEffect(() => {
    if (!enabled || !filterByProfiles) return;
    loadProfiles();
  }, [enabled, filterByProfiles, loadProfiles]);

  const profileCompanyIds = useMemo(() => {
    if (!filterByProfiles) return new Set();
    return new Set((profiles || []).map((p) => p.company_id).filter(Boolean));
  }, [filterByProfiles, profiles]);

  const profileCompanyNames = useMemo(() => {
    if (!filterByProfiles) return new Set();
    return new Set(
      (profiles || [])
        .map((p) => normalizeOption(p.company_name).toLowerCase())
        .filter(Boolean)
    );
  }, [filterByProfiles, profiles]);

  const effectiveCompanies = useMemo(() => {
    if (!filterByProfiles) return companies || [];
    return (companies || []).filter((c) => {
      if (profileCompanyIds.has(c.id)) return true;
      const name = normalizeOption(c?.name).toLowerCase();
      return name && profileCompanyNames.has(name);
    });
  }, [companies, filterByProfiles, profileCompanyIds, profileCompanyNames]);

  const getCompanyIdByName = useCallback(
    (name) => {
      const normalized = normalizeOption(name).toLowerCase();
      if (!normalized) return "";
      const match = (effectiveCompanies || []).find((c) => String(c?.name || "").toLowerCase() === normalized);
      return match?.id || "";
    },
    [effectiveCompanies]
  );

  const ensureProducts = useCallback(
    async (companyId) => {
      if (!companyId) return;
      if (productsByCompanyId[companyId]) return;
      if (loadingProducts[companyId]) return;

      setLoadingProducts((prev) => ({ ...prev, [companyId]: true }));
      try {
        const res = await api.get(`/commission/companies/${companyId}/products`);
        const list = Array.isArray(res.data) ? res.data : [];
        setProductsByCompanyId((prev) => ({ ...prev, [companyId]: list }));
      } catch {
        setProductsByCompanyId((prev) => ({ ...prev, [companyId]: [] }));
      } finally {
        setLoadingProducts((prev) => ({ ...prev, [companyId]: false }));
      }
    },
    [loadingProducts, productsByCompanyId]
  );

  const companyOptions = useMemo(() => {
    const names = (effectiveCompanies || []).map((c) => normalizeOption(c?.name));
    return sortOptions(names);
  }, [effectiveCompanies]);

  const getProductOptionsByCompanyId = useCallback(
    (companyId) => {
      const list = productsByCompanyId[companyId] || [];
      const names = list.map((p) => normalizeOption(p?.name));
      return sortOptions(names);
    },
    [productsByCompanyId]
  );

  const getProductOptionsByCompanyName = useCallback(
    (companyName) => {
      const id = getCompanyIdByName(companyName);
      if (!id) return [];
      return getProductOptionsByCompanyId(id);
    },
    [getCompanyIdByName, getProductOptionsByCompanyId]
  );

  return {
    companies: effectiveCompanies,
    companyOptions,
    loadingCompanies,
    loadingProfiles,
    ensureProducts,
    getCompanyIdByName,
    getProductOptionsByCompanyName,
    productsByCompanyId,
  };
}
